<template>
    <div class="visitor-management page-container">
        <VisitorDetailsModal 
            :show="showModal"
            :visitor="selectedVisitor"
            @close="closeModal"
        />
        
        <h2>Visitors</h2>
        
        <div class="search-container">
            <input 
                type="text" 
                v-model="searchQuery" 
                @input="filterVisitors"
                placeholder="Search visitors..." 
                class="search-input"
            >
        </div>

        <div class="visitor-grid">
            <div v-for="visitor in filteredVisitors" 
                 :key="visitor.id" 
                 class="visitor-card"
                 @click="openModal(visitor)">
                <div class="card-content">
                    <div class="visitor-info">
                        <h3>{{ visitor.first_name }} {{ visitor.last_name }}</h3>
                        <p class="company" v-if="visitor.company">{{ visitor.company }}</p>
                        <p class="email">{{ visitor.email }}</p>
                        <p class="phone" v-if="visitor.phone">{{ visitor.phone }}</p>
                    </div>
                    <div class="visit-info">
                        <span class="visit-label">Visited</span>
                        <span class="visit-time" v-if="visitor.last_visit">
                            ({{ formatTimeAgo(visitor.last_visit) }})
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { visitorService } from '../services/visitorService';
import { formatDistanceToNow } from 'date-fns';
import VisitorDetailsModal from './VisitorDetailsModal.vue';

export default {
    name: 'VisitorManagement',
    components: {
        VisitorDetailsModal
    },
    setup() {
        const visitors = ref([]);
        const searchQuery = ref('');
        const loading = ref(false);
        const showModal = ref(false);
        const selectedVisitor = ref(null);

        const loadVisitors = async () => {
            try {
                loading.value = true;
                const response = await visitorService.getAllVisitors();
                visitors.value = response;
            } catch (error) {
                console.error('Error loading visitors:', error);
            } finally {
                loading.value = false;
            }
        };

        const filterVisitors = () => {
            // Filter will be implemented here
        };

        const filteredVisitors = computed(() => {
            let result = visitors.value;
            
            // Apply search filter if query exists
            if (searchQuery.value) {
                const query = searchQuery.value.toLowerCase();
                result = result.filter(visitor => {
                    return visitor.first_name.toLowerCase().includes(query) ||
                           visitor.last_name.toLowerCase().includes(query) ||
                           visitor.email.toLowerCase().includes(query) ||
                           (visitor.company && visitor.company.toLowerCase().includes(query)) ||
                           (visitor.phone && visitor.phone.includes(query));
                });
            }
            
            // Sort by first name
            return result.sort((a, b) => 
                a.first_name.toLowerCase().localeCompare(b.first_name.toLowerCase())
            );
        });

        const formatTimeAgo = (date) => {
            if (!date) return '';
            return formatDistanceToNow(new Date(date), { addSuffix: true });
        };

        const openModal = (visitor) => {
            selectedVisitor.value = visitor;
            showModal.value = true;
        };

        const closeModal = () => {
            showModal.value = false;
            selectedVisitor.value = null;
        };

        // Load visitors when component mounts
        onMounted(() => {
            loadVisitors();
        });

        return {
            visitors,
            searchQuery,
            loading,
            filteredVisitors,
            filterVisitors,
            formatTimeAgo,
            showModal,
            selectedVisitor,
            openModal,
            closeModal
        };
    }
};
</script>

<style scoped>
.page-container {
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
    min-height: calc(100vh - 60px);
    position: relative;
    width: 100%;
    overflow-x: hidden;
}

.visitor-management {
    background-color: #ebf8ff;
    position: relative;
    width: 100%;
    overflow-x: hidden;
}

.search-container {
    margin-bottom: 2rem;
}

.search-input {
    width: 100%;
    max-width: 400px;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
}

.visitor-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
    width: 100%;
    box-sizing: border-box;
}

.visitor-card {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    padding: 1rem;
    width: 100%;
    box-sizing: border-box;
}

.visitor-info {
    flex: 1;
    text-align: left;
}

.visitor-info h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.1rem;
    color: #2c3e50;
    text-align: left;
}

.visit-info {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    min-width: 100px;
}

.visit-label {
    color: #4285f4;
    font-weight: 500;
    font-size: 0.9rem;
}

.visit-time {
    color: #666;
    font-size: 0.8rem;
    margin-top: 0.25rem;
}

.visitor-info p {
    margin: 0.25rem 0;
    color: #666;
    font-size: 0.9rem;
}

.card-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
}

* {
    box-sizing: border-box;
}

@media (max-width: 768px) {
    .page-container {
        padding: 1rem;
        padding-top: 60px;
        width: 100%;
    }
    
    .visitor-grid {
        grid-template-columns: 1fr;
    }
}
</style> 